import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type FC,
  type ReactNode,
} from "react";
import {
  acceptAllConsent,
  necessaryOnlyConsent,
  readConsent,
  writeConsent,
  type CookieConsent,
} from "src/libs/cookie-consent";
import { applyConsentToTracking, sendCurrentPageView } from "src/libs/analytics";

type CookieConsentContextValue = {
  consent: CookieConsent | null;
  isReady: boolean;
  bannerOpen: boolean;
  acceptAll: () => void;
  acceptNecessaryOnly: () => void;
  openBanner: () => void;
};

const CookieConsentContext = createContext<CookieConsentContextValue | undefined>(
  undefined
);

export const CookieConsentProvider: FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [consent, setConsent] = useState<CookieConsent | null>(null);
  const [isReady, setIsReady] = useState(false);
  const [bannerOpen, setBannerOpen] = useState(false);

  useEffect(() => {
    const stored = readConsent();
    setConsent(stored);
    setBannerOpen(!stored);
    setIsReady(true);
  }, []);

  const persist = useCallback((next: CookieConsent, hadChoice: boolean) => {
    writeConsent(next);
    if (hadChoice) {
      window.location.reload();
      return;
    }
    applyConsentToTracking(next);
    if (next.analytics) {
      sendCurrentPageView();
    }
    setConsent(next);
    setBannerOpen(false);
  }, []);

  const acceptAll = useCallback(() => {
    persist(acceptAllConsent(), Boolean(consent));
  }, [consent, persist]);

  const acceptNecessaryOnly = useCallback(() => {
    persist(necessaryOnlyConsent(), Boolean(consent));
  }, [consent, persist]);

  const openBanner = useCallback(() => {
    setBannerOpen(true);
  }, []);

  const value = useMemo(
    () => ({
      consent,
      isReady,
      bannerOpen,
      acceptAll,
      acceptNecessaryOnly,
      openBanner,
    }),
    [acceptAll, acceptNecessaryOnly, bannerOpen, consent, isReady, openBanner]
  );

  return (
    <CookieConsentContext.Provider value={value}>
      {children}
    </CookieConsentContext.Provider>
  );
};

export function useCookieConsent(): CookieConsentContextValue {
  const context = useContext(CookieConsentContext);
  if (!context) {
    throw new Error("useCookieConsent must be used within CookieConsentProvider");
  }
  return context;
}
